import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useOrbitAnimationDetails from "../../../../hooks/useOrbitAnimationDetails";
import useThermalHeatmap from "../../../../hooks/useThermalHeatmap";
import {
  GRADIENT_STOPS,
  FaceId,
  FACE_ORDER,
  THERMAL_UPDATE_INTERVAL_MS,
  INT_ORDER,
  type IntDeckId,
  INT_LABELS,
  FACE_LABELS,
  JSON_FACE_KEY_TO_ID,
  INT_KEY_MAP,
  PLAYBACK_SPEEDS,
  LEGEND_2D_BG,
} from "../constants/thermalConstants";

export type { FaceId, IntDeckId };
export { INT_ORDER, INT_LABELS, FACE_LABELS, PLAYBACK_SPEEDS };

export interface HeatmapRow {
  time_sec: number;
  nodes: Record<string, number>;
}

interface FaceSummary {
  face: string;
  label: string;
  min: number;
  max: number;
  avg: number;
}


interface ParsedHeatmapData {
  rows: HeatmapRow[];
  nodesPerFace: number;
  internalData: Record<string, (number | number[])[]>;
  t0Epoch: number;
}

export const parseHeatmapObject = (json: any): ParsedHeatmapData | null => {
  try {
    const thermal = json?.thermal;
    if (!thermal) return null;

    const nodesPerFace: number = thermal.meta?.nodesPerFace ?? 9;
    const timeUnixSec: number[] = thermal.time?.timeEpochSec ?? [];
    const t0 = timeUnixSec[0] ?? 0;
    const faces: Record<string, number[][]> = thermal.time?.series?.faces ?? thermal.series?.faces ?? {};
    const internalData: Record<string, (number | number[])[]> = thermal.time?.series?.internal ?? thermal.series?.internal ?? {};
    const rows: HeatmapRow[] = timeUnixSec.map((t, stepIdx) => {
      const nodes: Record<string, number> = {};
      for (const [jsonKey, faceId] of Object.entries(JSON_FACE_KEY_TO_ID)) {
        const faceSteps = faces[jsonKey];
        if (!faceSteps) continue;
        const stepNodes = faceSteps[stepIdx] ?? [];
        for (let n = 0; n < nodesPerFace; n++) {
          nodes[`${faceId}_N${n + 1}`] = stepNodes[n] ?? 0;
        }
      }
      // Store internal deck temperatures — handle both scalar (current) and array (future multi-node)
      for (const { api, internal } of INT_KEY_MAP) {
        const deckSteps = internalData[api];
        if (!deckSteps) continue;
        const stepVal = deckSteps[stepIdx];
        if (stepVal === undefined || stepVal === null) continue;
        if (Array.isArray(stepVal)) {
          for (let n = 0; n < nodesPerFace; n++) nodes[`${internal}_N${n + 1}`] = (stepVal as number[])[n] ?? 0;
        } else {
          for (let n = 0; n < nodesPerFace; n++) nodes[`${internal}_N${n + 1}`] = stepVal as number;
        }
      }
      return { time_sec: t - t0, nodes };
    });
    return { rows, nodesPerFace, internalData, t0Epoch: t0 };
  } catch {
    return null;
  }
};

export const buildSummary = (rows: HeatmapRow[], nodesPerFace: number): FaceSummary[] =>
  FACE_ORDER.map((face) => {
    if (!rows.length) return { face, label: FACE_LABELS[face], min: 0, max: 0, avg: 0 };
    let min = Infinity, max = -Infinity, sum = 0;
    for (const row of rows)
      for (let i = 1; i <= nodesPerFace; i++) {
        const v = row.nodes[`${face}_N${i}`] ?? 0;
        if (v < min) min = v;
        if (v > max) max = v;
        sum += v;
      }
    return { face, label: FACE_LABELS[face], min, max, avg: sum / (rows.length * nodesPerFace) };
  });

export const buildInternalSummary = (rows: HeatmapRow[], nodesPerFace: number): FaceSummary[] =>
  INT_ORDER.map((deck) => {
    if (!rows.length) return { face: deck, label: INT_LABELS[deck], min: 0, max: 0, avg: 0 };
    let min = Infinity, max = -Infinity, sum = 0;
    for (const row of rows)
      for (let i = 1; i <= nodesPerFace; i++) {
        const v = row.nodes[`${deck}_N${i}`] ?? 0;
        if (v < min) min = v;
        if (v > max) max = v;
        sum += v;
      }
    return { face: deck, label: INT_LABELS[deck], min, max, avg: sum / (rows.length * nodesPerFace) };
  });

export const formatTemp = (v: number) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}°C`;

export const findClosestIndex = (rows: HeatmapRow[], elapsed: number): number => {
  if (elapsed <= rows[0].time_sec) return 0;
  if (elapsed >= rows[rows.length - 1].time_sec) return rows.length - 1;
  let lo = 0, hi = rows.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (rows[mid].time_sec <= elapsed) lo = mid; else hi = mid;
  }
  return Math.abs(rows[lo].time_sec - elapsed) <= Math.abs(rows[hi].time_sec - elapsed) ? lo : hi;
};

export const formatTime = (sec: number) => {
  const h = Math.floor(sec / 3600).toString().padStart(2, "0");
  const m = Math.floor((sec % 3600) / 60).toString().padStart(2, "0");
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${h}:${m}:${s}`;
};

export function tempToColor(temp: number, min: number, max: number): string {
  const t = max === min ? 0.5 : Math.max(0, Math.min(1, (temp - min) / (max - min)));
  const idx = Math.max(
    1,
    GRADIENT_STOPS.findIndex(([s]) => s >= t),
  );
  const [s0, c0] = GRADIENT_STOPS[idx - 1];
  const [s1, c1] = GRADIENT_STOPS[idx];
  const u = (t - s0) / (s1 - s0);
  return `rgb(${c0.map((v, i) => Math.round(v + (c1[i] - v) * u)).join(",")})`;
}

export const useThermalHeatMapState = () => {
  const { getViewerCurrentTime } = useOrbitAnimationDetails();
  const { isThermalOpen: isOpen, setIsThermalOpen, thermalHeatmapData, thermalEnvParams } = useThermalHeatmap();
  const isThermalMonitor = isOpen;
  const [isMinimized, setIsMinimized] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSynced, setIsSynced] = useState(true);
  const [is3dView, setIs3dView] = useState(true);
  const [heatmapRows, setHeatmapRows] = useState<HeatmapRow[]>([]);
  const [nodesPerFace, setNodesPerFace] = useState(9);
  const [stepIndex, setStepIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [fetchStatus, setFetchStatus] = useState<"idle" | "loading" | "unavailable" | "loaded">("idle");
  const [t0Epoch, setT0Epoch] = useState<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isLastStep = stepIndex >= heatmapRows.length - 1;

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (!isPlaying || !heatmapRows.length) return;
    intervalRef.current = setInterval(() => {
      setStepIndex((prev) => {
        if (prev >= heatmapRows.length - 1) {
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, THERMAL_UPDATE_INTERVAL_MS / playbackSpeed);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, playbackSpeed, heatmapRows.length]);

  useEffect(() => {
    if (!isThermalMonitor) return;
    if (heatmapRows.length > 0) return;
    const parsed = parseHeatmapObject(thermalHeatmapData);
    if (!parsed || !parsed.rows.length) {
      setFetchStatus("unavailable");
      return;
    }
    setHeatmapRows(parsed.rows);
    setNodesPerFace(parsed.nodesPerFace);
    setT0Epoch(parsed.t0Epoch);
    setStepIndex(0);
    setIsPlaying(false);
    setFetchStatus("loaded");
  }, [isThermalMonitor, thermalHeatmapData, heatmapRows.length]);

  const handleToggleCompact = useCallback(() => {
    setIsCompact((c) => {
      if (!c) setIsFullscreen(false);
      return !c;
    });
  }, []);

  const handleToggleSync = useCallback(() => {
    setIsSynced((s) => {
      if (!s) { setIsPlaying(false); } // stopping manual playback when re-syncing
      return !s;
    });
  }, []);

  // Cesium clock → heatmap step sync
  useEffect(() => {
    if (!isSynced || !heatmapRows.length || t0Epoch === null) return;
    let rafId: number;
    let lastTick = 0;
    const MIN_TICK_MS = 100; // ~10Hz — plenty for discrete simulation rows
    const tick = (now: number) => {
      if (now - lastTick >= MIN_TICK_MS) {
        lastTick = now;
        const elapsed = getViewerCurrentTime() - t0Epoch;
        setStepIndex(findClosestIndex(heatmapRows, elapsed));
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isSynced, heatmapRows, t0Epoch, getViewerCurrentTime]);

  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen((f) => {
      if (!f) {
        setIsMinimized(false);
        setIsCompact(false);
      }
      return !f;
    });
  }, []);

  const handleTogglePlay = useCallback(() => {
    if (isLastStep) { setStepIndex(0); setIsPlaying(true); return; }
    setIsPlaying((p) => !p);
  }, [isLastStep]);

  const handleCycleSpeed = useCallback(() => {
    setPlaybackSpeed((s) => PLAYBACK_SPEEDS[(PLAYBACK_SPEEDS.indexOf(s) + 1) % PLAYBACK_SPEEDS.length]);
  }, []);

  const summary = useMemo(() => buildSummary(heatmapRows, nodesPerFace), [heatmapRows, nodesPerFace]);
  const internalSummary = useMemo(() => buildInternalSummary(heatmapRows, nodesPerFace), [heatmapRows, nodesPerFace]);

  const { globalMin, globalMax, summaryItems } = useMemo(() => {
    if (!heatmapRows.length) return { globalMin: null, globalMax: null, summaryItems: [] };
    let globalMin = Infinity, globalMax = -Infinity, avgSum = 0;
    let globalMinFace: FaceSummary | undefined, globalMaxFace: FaceSummary | undefined;
    for (const s of summary) {
      if (s.min < globalMin) { globalMin = s.min; globalMinFace = s; }
      if (s.max > globalMax) { globalMax = s.max; globalMaxFace = s; }
      avgSum += s.avg;
    }
    const globalAvg = summary.length ? avgSum / summary.length : 0;
    return {
      globalMin,
      globalMax,
      summaryItems: [
        { label: "Max Surface", value: formatTemp(globalMax), face: globalMaxFace?.label, color: "#FF3333" },
        { label: "Min Surface", value: formatTemp(globalMin), face: globalMinFace?.label, color: "#47B7FF" },
        { label: "Average Temp", value: formatTemp(globalAvg), color: "#CCF54E" },
      ],
    };
  }, [summary, heatmapRows.length]);

  const envParams = useMemo(() => ({
    albedo_factor: thermalEnvParams?.albedo_factor ?? null,
    blackbody_temperature: thermalEnvParams?.blackbody_temperature ?? null,
    solar_constant: thermalEnvParams?.solar_constant ?? null,
  }), [thermalEnvParams]);

  return {
    isThermalMonitor,
    setIsThermalOpen,
    isMinimized,
    setIsMinimized,
    isCompact,
    isFullscreen,
    isSynced,
    is3dView,
    setIs3dView,
    heatmapRows,
    nodesPerFace,
    stepIndex,
    isPlaying,
    playbackSpeed,
    fetchStatus,
    isLastStep,
    handleToggleCompact,
    handleToggleSync,
    handleToggleFullscreen,
    handleTogglePlay,
    handleCycleSpeed,
    summary,
    internalSummary,
    globalMin,
    globalMax,
    summaryItems,
    envParams,
    t0Epoch,
  };
};
